import { useCallback } from 'react';
import i18n from 'i18next';
import { useNavigationStore } from '@/stores/navigation-store';
import { useCompanyStore } from '@/stores/company-store';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { getToken, useSnackbarStore } from '@certara/common-react-components';
import Cookies from 'js-cookie';

export interface UseFileOperationsOptions {
  onError?: (error: Error) => void;
  onSuccess?: (message: string) => void;
}

export function useFileOperations(options: UseFileOperationsOptions = {}) {
  const { activeCompany } = useCompanyStore();
  const { currentPath, navigateToFolder } = useNavigationStore();
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  /**
   * Delete one or more files/folders
   */
  const deleteItems = useCallback(
    async (itemIds: string[], itemNames: string[], isFolder?: boolean[]): Promise<boolean> => {
      if (!activeCompany) return false;
      try {
        // Build objects array matching Vue app format
        const objects = itemIds.map((_, index) => ({
          IsFolder: isFolder?.[index] ?? true,
          Path: itemNames[index],
        }));

        // Use postForm to match Vue's jQuery.ajax behavior (form-encoded data)
        const response = await apiClient.postForm<Array<{ IsSuccess: boolean; Path: string; ErrorMessage?: string }>>(
          'api/Storage/Delete',
          {
            companyId: activeCompany.Id,
            objects,
            currentLocationPath: currentPath || '',
          }
        );

        if (response && Array.isArray(response)) {
          const failed = response.filter((x) => !x.IsSuccess);
          if (failed.length > 0) {
            addSnackbar(
              i18n.t('load:hooks.fileOps.deleteFailed', { names: failed.map((x) => x.Path).join(', ') }),
              'error',
              failed.map((x) => `${x.Path}: ${x.ErrorMessage}`).join('\n')
            );
          } else {
            queryClient.invalidateQueries({ queryKey: ['gridData'] });
            addSnackbar(i18n.t('load:hooks.fileOps.deleteSuccess'), 'success');
            return true;
          }
        }
        return false;
      } catch (error: unknown) {
        const message = i18n.t('load:hooks.fileOps.deleteError', { error: error instanceof Error ? error.message : String(error) });
        addSnackbar(message, 'error');
        options.onError?.(error as Error);
        return false;
      }
    },
    [activeCompany, currentPath, queryClient, addSnackbar, options]
  );

  /**
   * Rename a single file/folder
   * Calls api/Storage/RenameFile with companyId, path, oldFileName, newFileName
   */
  const renameItem = useCallback(
    async (oldName: string, newName: string): Promise<boolean> => {
      if (!activeCompany) return false;
      try {
        const response = await apiClient.postForm<{ IsSuccess: boolean; ErrorMessage?: string }>(
          'api/Storage/RenameFile',
          {
            companyId: activeCompany.Id,
            path: currentPath || '',
            oldFileName: oldName,
            newFileName: newName,
          }
        );
        if (response?.IsSuccess) {
          queryClient.invalidateQueries({ queryKey: ['gridData'] });
          addSnackbar(i18n.t('load:hooks.fileOps.renamedSuccess', { oldName, newName }), 'success');
          return true;
        } else {
          const errorMsg = response?.ErrorMessage || i18n.t('load:hooks.fileOps.renameFailed');
          addSnackbar(errorMsg, 'error');
          return false;
        }
      } catch (error: unknown) {
        const message = i18n.t('load:hooks.fileOps.renameError', { error: error instanceof Error ? error.message : String(error) });
        addSnackbar(message, 'error');
        options.onError?.(error as Error);
        return false;
      }
    },
    [activeCompany, currentPath, queryClient, addSnackbar, options]
  );

  /**
   * Rename multiple files/folders (one at a time using RenameFile endpoint)
   */
  const renameItems = useCallback(
    async (items: Array<{ oldName: string; newName: string }>): Promise<boolean> => {
      if (!activeCompany) return false;
      
      let successCount = 0;
      let failCount = 0;
      
      for (const item of items) {
        try {
          const response = await apiClient.postForm<{ IsSuccess: boolean; ErrorMessage?: string }>(
            'api/Storage/RenameFile',
            {
              companyId: activeCompany.Id,
              path: currentPath || '',
              oldFileName: item.oldName,
              newFileName: item.newName,
            }
          );
          if (response?.IsSuccess) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['gridData'] });
      
      if (failCount === 0) {
        addSnackbar(i18n.t('load:hooks.fileOps.batchRenameSuccess', { count: successCount }), 'success');
        return true;
      } else if (successCount > 0) {
        addSnackbar(i18n.t('load:hooks.fileOps.batchRenamePartial', { success: successCount, failed: failCount }), 'warning');
        return true;
      } else {
        addSnackbar(i18n.t('load:hooks.fileOps.batchRenameFailed', { count: failCount }), 'error');
        return false;
      }
    },
    [activeCompany, currentPath, queryClient, addSnackbar]
  );

  /**
   * Download file(s) - triggers browser download using same approach as toolbar
   */
  const downloadItem = useCallback(
    async (
      _itemId: string,
      itemName: string,
      isFolder: boolean = false
    ): Promise<boolean> => {
      if (!activeCompany) return false;
      try {
        // Get access token and set authorization cookie
        // NOTE: Cookie-based auth is required because the backend StorageDownloadController
        // uses [ActionRequestFilter(UseCookie = true)] which reads auth from a cookie, not
        // a header. This matches the Vue app's behavior. The cookie is:
        // - Short-lived (30 seconds)
        // - SameSite: strict (prevents CSRF)
        // - Base64 encoded (not encrypted, but backend validates the actual token)
        // Changing this would require backend changes to accept header-based auth for downloads.
        const token = await getToken();

        if (token) {
          const expiresDate = new Date();
          expiresDate.setTime(expiresDate.getTime() + 30 * 1000); // 30 seconds
          Cookies.set('authorization', btoa(`Bearer ${token}`), {
            sameSite: 'strict',
            expires: expiresDate,
          });
        }

        // Create download object for the item
        const objects = [{
          IsFolder: isFolder,
          Path: itemName,
        }];

        // Build URL params
        const urlParams = new URLSearchParams();
        urlParams.set('companyId', activeCompany.Id.toString());
        urlParams.set('currentFolderPath', currentPath || '');
        urlParams.set('objects', JSON.stringify(objects));

        // Build the download URL
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const downloadPath = 'api/StorageDownload/Download';
        const downloadUrl = apiBaseUrl
          ? `${apiBaseUrl}/${downloadPath}?${urlParams.toString()}`
          : `${downloadPath}?${urlParams.toString()}`;

        // Create and click download link
        const link = document.createElement('a');
        link.setAttribute('download', '');
        link.href = downloadUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();

        addSnackbar(i18n.t('load:hooks.fileOps.downloadStarting'), 'success');
        return true;
      } catch (error: unknown) {
        const message = i18n.t('load:hooks.fileOps.downloadError', { error: error instanceof Error ? error.message : String(error) });
        addSnackbar(message, 'error');
        options.onError?.(error as Error);
        return false;
      }
    },
    [activeCompany, currentPath, addSnackbar, options]
  );

  /**
   * Download multiple files
   */
  const downloadItems = useCallback(
    async (
      items: Array<{
        itemId: string;
        itemName: string;
        isFolder?: boolean;
      }>
    ) => {
      // Download each item one at a time
      for (const item of items) {
        await downloadItem(
          item.itemId,
          item.itemName,
          item.isFolder || false
        );
        // Small delay between downloads
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    },
    [downloadItem]
  );

  /**
   * Create a new folder
   */
  const createFolder = useCallback(
    async (folderName: string): Promise<boolean> => {
      if (!activeCompany) return false;
      try {
        const response = await apiClient.post<{ success: boolean }>('api/storage/CreateFolder', {
          companyId: activeCompany.Id,
          path: currentPath,
          folderName,
        });

        if (response?.success) {
          queryClient.invalidateQueries({ queryKey: ['gridData'] });
          addSnackbar(i18n.t('load:hooks.fileOps.folderCreated', { name: folderName }), 'success');
          return true;
        } else {
          throw new Error(i18n.t('load:hooks.fileOps.folderCreationFailed'));
        }
      } catch (error: unknown) {
        const message = i18n.t('load:hooks.fileOps.folderCreateError', { error: error instanceof Error ? error.message : String(error) });
        addSnackbar(message, 'error');
        options.onError?.(error as Error);
        return false;
      }
    },
    [activeCompany, currentPath, queryClient, addSnackbar, options]
  );

  /**
   * Navigate to a folder
   */
  const goToFolder = useCallback(
    (path: string) => {
      navigateToFolder(path);
    },
    [navigateToFolder]
  );

  /**
   * Go back to parent folder
   */
  const goToParent = useCallback(() => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    const parentPath = parts.join('/');
    navigateToFolder(parentPath);
  }, [currentPath, navigateToFolder]);

  return {
    deleteItems,
    renameItem,
    renameItems,
    downloadItem,
    downloadItems,
    createFolder,
    goToFolder,
    goToParent,
  };
}